import React, { useState } from 'react';
import { Button, Card, Typography, Tag, Space, Tabs, Modal, Input, Upload, message, ColorPicker } from 'antd';
import { UploadOutlined, DownloadOutlined, FileTextOutlined, CheckCircleFilled } from '@ant-design/icons';
import { ITEM_STATUS } from './materialConfig';
import ChannelsDrawer from './ChannelsDrawer';

const { Text } = Typography;
const { TextArea } = Input;

const actionLabel = (mode) => (mode === 'file' ? '上传' : '填写');

/**
 * 资料清单（阶段 Tabs + 分组 + 资料项）
 * - 普通项（text/file/color/image）：行内一个操作，弹窗保存
 * - 渠道项（channels）：行内列出各渠道状态，点「配置」打开抽屉（左渠道 / 右字段）
 */
const CustomChecklist = ({ checklist, itemStates, isService, onItemSave, defaultStage, channelSync, uiTab, readOnly = false }) => {
    const [editModal, setEditModal] = useState({ open: false, item: null });
    const [editValue, setEditValue] = useState('');
    const [editThumb, setEditThumb] = useState('');
    const [viewModal, setViewModal] = useState({ open: false, item: null });
    const [channelsModal, setChannelsModal] = useState({ open: false, item: null, initialKey: null });

    const stageItems = (s) => s.sections.flatMap((sec) => sec.items).filter((it) => it.mode !== 'import');
    const stageStats = (s) => {
        const items = stageItems(s);
        const saved = items.filter((it) => itemStates[it.key]?.status === 'draft').length;
        return { total: items.length, saved };
    };

    const openEdit = (it) => {
        setEditValue(itemStates[it.key]?.value || '');
        setEditThumb(itemStates[it.key]?.thumbUrl || '');
        setEditModal({ open: true, item: it });
    };

    const handleEditOk = () => {
        const { item } = editModal;
        if (item.mode === 'color' || item.mode === 'file') {
            if (!editValue) { message.warning(item.mode === 'color' ? '请选择颜色' : (item.kind === 'image' ? '请先上传图片' : '请先上传文件')); return; }
        } else if (!editValue.trim()) {
            message.warning('请填写内容');
            return;
        }
        onItemSave(item.key, {
            status: 'draft',
            value: editValue,
            ...(item.kind === 'image' ? { thumbUrl: editThumb } : {}),
        });
        setEditModal({ open: false, item: null });
        setEditValue('');
        setEditThumb('');
        message.success('已保存');
    };

    // ── 渠道项 ──
    const chanConfigured = (state, key) => Object.values(state.config?.data?.[key] || {}).some((v) => v && String(v).trim());
    const chanVisible = (item, state, key) => (item.selectable ? (state.config?.selected || []).includes(key) : true);
    const openChannel = (item, key) => setChannelsModal({ open: true, item, initialKey: key || null });
    const handleChannelsSave = (status, summary, config) => {
        onItemSave(channelsModal.item.key, { status, value: summary, config });
        setChannelsModal({ open: false, item: null, initialKey: null });
    };

    const renderChannelsItem = (it) => {
        const state = itemStates[it.key] || {};
        const st = ITEM_STATUS[state.status || 'pending'];
        const channels = it.channels || [];
        const visible = channels.filter((c) => chanVisible(it, state, c.key));
        return (
            <div key={it.key} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <Space size={8}>
                        <Text strong style={{ fontSize: 13 }}>{it.name}</Text>
                        <Tag color={st.color} style={{ border: 'none' }}>{st.text}</Tag>
                    </Space>
                    {it.selectable && !readOnly && (
                        <Button type="link" size="small" onClick={() => openChannel(it)}>{it.manageLabel || '管理渠道'}</Button>
                    )}
                </div>
                <div><Text type="secondary" style={{ fontSize: 12 }}>{it.desc}</Text></div>
                {visible.length ? visible.map((c) => {
                    const conf = chanConfigured(state, c.key);
                    return (
                        <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0 8px 8px', borderTop: '1px solid #fafafa' }}>
                            <Space size={8}>
                                <Text style={{ fontSize: 13 }}>{c.name}</Text>
                                {c.push === false && <Tag style={{ border: 'none' }}>无推送</Tag>}
                                <Tag color={conf ? 'success' : 'default'} style={{ border: 'none' }}>{conf ? '已配置' : '未配置'}</Tag>
                            </Space>
                            <Button type="link" size="small" disabled={readOnly} onClick={() => openChannel(it, c.key)}>配置</Button>
                        </div>
                    );
                }) : <div style={{ padding: '8px 0' }}><Text type="secondary" style={{ fontSize: 12 }}>尚无渠道，点右上角「{it.manageLabel || '管理渠道'}」选择</Text></div>}
            </div>
        );
    };

    // ── 普通项 ──
    const renderValue = (state, it) => {
        if (it.mode === 'color') {
            if (!state.value) return null;
            return (
                <div style={{ marginTop: 6 }}>
                    <Space size={8}>
                        <span style={{ display: 'inline-block', width: 20, height: 20, borderRadius: 4, border: '1px solid #d9d9d9', background: state.value, verticalAlign: 'middle' }} />
                        <Text code style={{ fontSize: 12 }}>{state.value}</Text>
                    </Space>
                </div>
            );
        }
        if (!state.value) return null;
        if (it.kind === 'image' && state.thumbUrl) {
            return (
                <div style={{ marginTop: 6 }}>
                    <img src={state.thumbUrl} alt={state.value} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #f0f0f0', background: '#fafafa' }} />
                </div>
            );
        }
        return (
            <div style={{ marginTop: 6, maxWidth: 560 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, padding: '3px 10px' }}>
                    <CheckCircleFilled style={{ color: '#52c41a', fontSize: 13, marginRight: 6, flexShrink: 0 }} />
                    <Text style={{ fontSize: 12, color: '#237804' }} ellipsis={{ tooltip: state.value }}>{state.value}</Text>
                </span>
            </div>
        );
    };

    const renderActions = (it) => {
        const hasValue = itemStates[it.key]?.status === 'draft';
        const viewBtn = hasValue ? <Button type="link" size="small" onClick={() => setViewModal({ open: true, item: it })}>查看</Button> : null;
        // 只读预览：仅可查看已保存内容，不提供填写 / 上传 / 代填 / 配置
        if (readOnly) return viewBtn;
        if (isService) {
            return <Space size={0}>{viewBtn}<Button type="link" size="small" onClick={() => openEdit(it)}>代填</Button></Space>;
        }
        if (hasValue) {
            return <Space size={0}>{viewBtn}<Button type="link" size="small" onClick={() => openEdit(it)}>编辑</Button></Space>;
        }
        return <Button type="link" size="small" onClick={() => openEdit(it)}>{actionLabel(it.mode)}</Button>;
    };

    const renderItemRow = (it, idx, total) => {
        const state = itemStates[it.key] || {};
        const st = ITEM_STATUS[state.status || 'pending'];
        return (
            <div key={it.key} style={{
                display: 'flex', justifyContent: 'space-between', gap: 16, padding: '12px 0',
                borderBottom: idx === total - 1 ? 'none' : '1px solid #f0f0f0',
            }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Space size={8} wrap>
                        <Text strong style={{ fontSize: 13 }}>{it.name}</Text>
                        <Tag color={st.color} style={{ border: 'none' }}>{st.text}</Tag>
                    </Space>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>{it.desc}</Text></div>
                    {renderValue(state, it)}
                </div>
                <div style={{ whiteSpace: 'nowrap' }}>{renderActions(it)}</div>
            </div>
        );
    };

    const handleImport = (it, isUpdate) => {
        Modal.confirm({
            title: isUpdate ? '更新商户号' : '一键导入支付',
            content: isUpdate ? '将按最新配置同步商户号相关配置，确认更新？' : '将把二阶段已配置的支付渠道一键导入企业商户号，确认导入？',
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                onItemSave(it.key, { status: 'draft', value: isUpdate ? '已更新' : '已导入' });
                message.success(isUpdate ? '商户号已更新' : '导入完成');
            },
        });
    };

    // 一键导入支付 / 更新商户号（仅管理端）
    const renderImportItem = (it) => {
        if (!isService || readOnly) return null;
        const saved = itemStates[it.key]?.status === 'draft';
        return (
            <div key={it.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div>
                    <Text strong style={{ fontSize: 13 }}>{it.name}</Text>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>{it.desc}</Text></div>
                </div>
                <Button type={saved ? 'default' : 'primary'} onClick={() => handleImport(it, saved)}>
                    {saved ? '更新商户号' : '一键导入支付'}
                </Button>
            </div>
        );
    };

    const renderSection = (section) => (
        <Card key={section.key} size="small" title={section.title} style={{ marginBottom: 16, borderRadius: 8 }}
            styles={{ header: { background: '#fafafa' } }}>
            {section.items.map((it, idx) => (
                it.mode === 'channels' ? renderChannelsItem(it)
                    : it.mode === 'import' ? renderImportItem(it)
                        : renderItemRow(it, idx, section.items.length)
            ))}
        </Card>
    );

    const viewState = itemStates[viewModal.item?.key] || {};

    const stageTabs = checklist.map((s) => {
        const { total, saved } = stageStats(s);
        return {
            key: s.key,
            label: `${s.title}（${saved}/${total}）`,
            children: s.sections.length ? s.sections.map(renderSection) : <Text type="secondary">该阶段无需客户提供资料</Text>,
        };
    });
    // Tab 顺序贴合流程：开发一阶段 → UI设计 → 开发二阶段（无定位参数时默认落在「开发一阶段」）
    const tabItems = [
        ...stageTabs.slice(0, 1),
        ...(uiTab ? [{ key: 'ui', label: 'UI设计', children: uiTab }] : []),
        ...stageTabs.slice(1),
    ];

    return (
        <>
            <Tabs
                defaultActiveKey={defaultStage || undefined}
                items={tabItems}
            />

            {/* ── 填写 / 上传 / 取色 ── */}
            <Modal
                title={`${isService ? '代填' : editModal.item?.mode === 'file' ? '上传' : '填写'}：${editModal.item?.name || ''}`}
                open={editModal.open}
                onOk={handleEditOk}
                onCancel={() => { setEditModal({ open: false, item: null }); setEditValue(''); setEditThumb(''); }}
                okText={isService ? '代填保存' : '保存'}
                cancelText="取消"
                width={520}
                destroyOnClose
            >
                <div style={{ marginBottom: 12 }}><Text type="secondary" style={{ fontSize: 12 }}>{editModal.item?.desc}</Text></div>
                {editModal.item?.mode === 'color' ? (
                    <Space>
                        <ColorPicker value={editValue || '#1677ff'} showText
                            onChange={(c) => setEditValue(c?.toHexString ? c.toHexString() : String(c))} />
                        <Text type="secondary" style={{ fontSize: 12 }}>选择或输入主题色</Text>
                    </Space>
                ) : editModal.item?.mode === 'file' && editModal.item?.kind === 'image' ? (
                    <Upload listType="picture-card" maxCount={1} showUploadList={false}
                        beforeUpload={(file) => { setEditValue(file.name); setEditThumb(URL.createObjectURL(file)); return false; }}>
                        {editThumb
                            ? <img src={editThumb} alt="logo" style={{ width: '100%' }} />
                            : <div><UploadOutlined /><div style={{ marginTop: 4 }}>选择图片</div></div>}
                    </Upload>
                ) : editModal.item?.mode === 'file' ? (
                    <Upload beforeUpload={(file) => { setEditValue(file.name); return false; }} maxCount={1}>
                        <Button icon={<UploadOutlined />}>选择文件</Button>
                    </Upload>
                ) : (
                    <TextArea rows={4} placeholder="请输入内容" value={editValue} onChange={(e) => setEditValue(e.target.value)} />
                )}
                {editValue && editModal.item?.mode !== 'color' && (
                    <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>当前：{editValue}</Text></div>
                )}
            </Modal>

            {/* ── 渠道配置抽屉 ── */}
            <ChannelsDrawer
                open={channelsModal.open}
                item={channelsModal.item}
                value={channelsModal.item ? itemStates[channelsModal.item.key] : null}
                isService={isService}
                initialKey={channelsModal.initialKey}
                syncConfig={channelSync}
                onClose={() => setChannelsModal({ open: false, item: null, initialKey: null })}
                onSave={handleChannelsSave}
            />

            {/* ── 查看已保存资料 ── */}
            <Modal
                title={`查看资料：${viewModal.item?.name || ''}`}
                open={viewModal.open}
                onCancel={() => setViewModal({ open: false, item: null })}
                footer={<Button onClick={() => setViewModal({ open: false, item: null })}>关闭</Button>}
                width={520}
                destroyOnClose
            >
                {viewModal.item?.kind === 'image' && viewState.thumbUrl ? (
                    <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, textAlign: 'center' }}>
                        <img src={viewState.thumbUrl} alt={viewState.value} style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain' }} />
                        <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>{viewState.value}</Text></div>
                    </div>
                ) : viewModal.item?.mode === 'color' ? (
                    <div style={{ padding: 12, background: '#fafafa', borderRadius: 8 }}>
                        <Space size={8}>
                            <span style={{ display: 'inline-block', width: 28, height: 28, borderRadius: 6, border: '1px solid #d9d9d9', background: viewState.value, verticalAlign: 'middle' }} />
                            <Text code>{viewState.value}</Text>
                        </Space>
                    </div>
                ) : (
                    <div style={{ padding: 12, background: '#fafafa', borderRadius: 8 }}>
                        {viewModal.item?.mode === 'file' ? (
                            <Space>
                                <FileTextOutlined />
                                <Text>{viewState.value || '-'}</Text>
                                {viewState.value && <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => message.success('开始下载（mock）')}>下载</Button>}
                            </Space>
                        ) : (
                            <Text>{viewState.value || '-'}</Text>
                        )}
                    </div>
                )}
            </Modal>
        </>
    );
};

export default CustomChecklist;
